/**
 * @ub/shared/branchDay — «Часы работы филиала» и общий расход филиала (05.10.2026, владелец:
 * «прикрепить общий расходник к филиалу… для электроэнергии нужно просто посмотреть, сколько
 * времени филиал работал»).
 *
 * День филиала — смена 06:00 → 06:00: ночная игра (00:30) хранится датой следующего дня, но
 * принадлежит этому (dates.ts). Время работы по умолчанию — от начала первой игры до конца
 * последней; конец игры = игра + чайная + мастер-класс + уборка. Ручная поправка ОО/ЕО
 * (таблица branch_days) важнее подсчёта. Норма расхода — на час работы или на рабочий день.
 *
 * Чистые функции без зависимостей — исполняются node'ом напрямую (type stripping), как teaSpan.
 */

const DAY_START = 6 * 60;

/** Минуты от начала смены с ночным хвостом после вечера: 01:00 → 25:00. */
function dayMin(t: string): number {
  const [h, m] = String(t).slice(0, 5).split(":").map(Number);
  const v = (h || 0) * 60 + (m || 0);
  return v < DAY_START ? v + 1440 : v;
}

function hhmm(v: number): string {
  const x = ((Math.round(v) % 1440) + 1440) % 1440;
  return `${String(Math.floor(x / 60)).padStart(2, "0")}:${String(x % 60).padStart(2, "0")}`;
}

/** Игра дня филиала: время начала и сколько минут она держит филиал открытым. */
export interface DayGame { start: string; minutes: number }

/** Промежуток работы: начало, конец (HH:MM, конец может быть после полуночи) и длина в минутах. */
export interface DaySpan { from: string; to: string; minutes: number }

/** От начала первой игры до конца последней. Нет игр — null. */
export function gamesSpan(games: readonly DayGame[]): DaySpan | null {
  let lo = Infinity;
  let hi = -Infinity;
  for (const g of games) {
    const s = dayMin(g.start);
    lo = Math.min(lo, s);
    hi = Math.max(hi, s + Math.max(0, Number(g.minutes) || 0));
  }
  if (!Number.isFinite(lo)) return null;
  return { from: hhmm(lo), to: hhmm(hi), minutes: Math.max(0, hi - lo) };
}

/** Длина ручного промежутка «с — по»: «по» не позже «с» значит после полуночи (11:00–01:00 = 14 ч). */
export function spanMinutes(from: string, to: string): number {
  const a = dayMin(from);
  let b = dayMin(to);
  if (b <= a) b += 1440;
  return Math.min(1440, b - a);
}

/** Бизнес-дата игры: ночные (раньше 06:00) относятся к предыдущему дню. ISO-даты, чистый UTC. */
export function businessDate(date: string, time: string): string {
  if (String(time).slice(0, 5) >= "06:00") return date;
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

/**
 * Расход за день по норме филиала. perHour: qty на каждый час работы; иначе qty за день, если
 * филиал работал. roundTo > 0 — вверх до кратного (как у остальных норм). Два знака после запятой.
 */
export function branchUsage(qty: number, perHour: boolean, minutes: number, roundTo = 0): number {
  const m = Math.max(0, Number(minutes) || 0);
  const q = Math.max(0, Number(qty) || 0);
  let v = perHour ? (q * m) / 60 : m > 0 ? q : 0;
  const r = Math.max(0, Math.floor(Number(roundTo) || 0));
  if (r > 0 && v > 0) v = Math.ceil(v / r - 1e-9) * r;
  return Math.round(v * 100) / 100;
}

/** «13 ч 15 мин» / «45 мин» / «0». */
export function fmtWorkMinutes(minutes: number): string {
  const m = Math.max(0, Math.round(Number(minutes) || 0));
  if (!m) return "0";
  const h = Math.floor(m / 60);
  const r = m % 60;
  return h ? (r ? `${h} ч ${r} мин` : `${h} ч`) : `${r} мин`;
}
